import React, { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";

const fmt = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const fmtTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

// ─── AnimatedScore ─────────────────────────────────────────────────────────────
const AnimatedScore = ({ score, color }) => {
  const [bump, setBump] = useState(false);
  const prevScore = useRef(score);

  useEffect(() => {
    if (score !== prevScore.current) {
      prevScore.current = score;
      setBump(true);
      const t = setTimeout(() => setBump(false), 300);
      return () => clearTimeout(t);
    }
  }, [score]);

  return (
    <span
      className={`tabular-nums font-black transition-transform duration-150 text-5xl ${bump ? "scale-125" : "scale-100"} inline-block`}
      style={{ color }}
    >
      {score}
    </span>
  );
};

// ─── LivePulse ────────────────────────────────────────────────────────────────
const LivePulse = () => (
  <div className="flex items-center gap-1.5">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
    </span>
    <span className="text-xs font-black tracking-widest uppercase text-red-500 dark:text-red-400">
      LIVE
    </span>
  </div>
);

// ─── LoadingState ─────────────────────────────────────────────────────────────
const LoadingState = () => (
  <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-500 dark:text-gray-400 text-sm tracking-widest uppercase">
        Loading scoreboard...
      </p>
    </div>
  </div>
);

// ─── NextGameState ────────────────────────────────────────────────────────────
const NextGameState = ({ nextGame }) => (
  <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex flex-col items-center justify-center px-6 gap-8">
    {/* ... same as before, but with dark: classes ... */}
    <div className="text-center">
      <p className="text-gray-500 dark:text-gray-400 text-xs font-bold tracking-[0.3em] uppercase mb-2">
        Muqalab Hoops
      </p>
      <div className="w-12 h-px bg-gray-300 dark:bg-gray-700 mx-auto" />
    </div>

    <div className="text-center">
      <p className="text-gray-500 dark:text-gray-400 text-xs font-bold tracking-[0.25em] uppercase mb-6">
        No games in progress
      </p>
      {nextGame ? (
        <div className="bg-white dark:bg-gray-900 rounded-3xl px-8 py-7 border border-gray-200 dark:border-gray-800 max-w-sm w-full">
          <p className="text-gray-500 dark:text-gray-400 text-xs font-bold tracking-[0.2em] uppercase mb-5">
            Next game
          </p>
          {/* rest unchanged */}
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="text-center flex-1">
              <p className="text-gray-900 dark:text-white font-bold text-lg leading-tight">
                {nextGame.team1?.name ?? "—"}
              </p>
            </div>
            <div className="text-gray-400 dark:text-gray-600 font-black text-xl shrink-0">
              vs
            </div>
            <div className="text-center flex-1">
              <p className="text-gray-900 dark:text-white font-bold text-lg leading-tight">
                {nextGame.team2?.name ?? "—"}
              </p>
            </div>
          </div>
          {nextGame.game_date && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-4 text-center space-y-0.5">
              <p className="text-orange-600 dark:text-orange-400 font-semibold text-sm">
                {fmt(nextGame.game_date)}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">
                {fmtTime(nextGame.game_date)}
              </p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          No upcoming games scheduled.
        </p>
      )}
    </div>
    <p className="text-gray-500 dark:text-gray-400 text-xs tracking-wider">
      This page updates automatically
    </p>
  </div>
);

// ─── Last Action Ticker ───────────────────────────────────────────────────────
const LastAction = ({ action }) => {
  if (!action) return null;

  const descMap = {
    points: (a) =>
      a.points === 3 ? "+3 (3PM)" : a.points === 2 ? "+2 (2PM)" : "+1 (FT)",
    rebounds: () => "Rebound",
    assists: () => "Assist",
    personalFouls: () => "Personal Foul",
    technicalFouls: () => "Technical Foul",
    threePm: () => "+3 (3PM)",
    twoPm: () => "+2 (2PM)",
    ftMade: () => "+1 (FT)",
  };

  const description = descMap[action.stat]?.(action) ?? action.stat;

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl px-5 py-3 mt-4 max-w-md mx-auto">
      <div className="flex items-center gap-3 text-sm">
        <div className="text-orange-500 dark:text-orange-400 font-bold text-xs tracking-widest uppercase">
          Last Action
        </div>
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
      </div>
      <p className="mt-1 text-gray-800 dark:text-gray-200 font-medium text-sm">
        {action.player_name}{" "}
        <span className="text-gray-500 dark:text-gray-400">
          #{action.jersey}
        </span>
        {" · "}
        <span className="font-semibold text-orange-600 dark:text-orange-400">
          {action.team_name}
        </span>
        {" · "}
        <span className="font-mono text-emerald-600 dark:text-emerald-400">
          {description}
        </span>
      </p>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
        {new Date(action.ts).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
        })}
      </p>
    </div>
  );
};

// ─── GameCard (updated) ───────────────────────────────────────────────────────
const GameCard = ({ game, isFinal, lastAction }) => {
  const halfLabel = game.half === 1 ? "1st Half" : "2nd Half";

  const winnerName = isFinal
    ? game.team1_score > game.team2_score
      ? game.team1?.name
      : game.team2_score > game.team1_score
        ? game.team2?.name
        : null
    : null;

  const isDark = isDarkModeActive();
  const ts1 = getTeamStyle(game.team1);
  const ts2 = getTeamStyle(game.team2);

  // Score colors: pick whichever of primary/accent contrasts better against the card bg
  const score1Color =
    Math.abs(
      parseInt(ts1.primaryHex.replace("#", "").slice(0, 2), 16) * 0.2126 +
        parseInt(ts1.primaryHex.replace("#", "").slice(2, 4), 16) * 0.7152 +
        parseInt(ts1.primaryHex.replace("#", "").slice(4, 6), 16) * 0.0722 -
        (isDark ? 30 : 200),
    ) >= 60
      ? ts1.primaryHex
      : ts1.accentHex;
  const score2Color =
    Math.abs(
      parseInt(ts2.primaryHex.replace("#", "").slice(0, 2), 16) * 0.2126 +
        parseInt(ts2.primaryHex.replace("#", "").slice(2, 4), 16) * 0.7152 +
        parseInt(ts2.primaryHex.replace("#", "").slice(4, 6), 16) * 0.0722 -
        (isDark ? 30 : 200),
    ) >= 60
      ? ts2.primaryHex
      : ts2.accentHex;

  // Logo border colors from team accent
  const logo1BorderColor = `${ts1.accentHex}60`;
  const logo2BorderColor = `${ts2.accentHex}60`;

  return (
    <div className="relative bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-lg">
      {/* Final overlay */}
      {isFinal && (
        <div className="absolute inset-0 bg-gray-950/95 flex flex-col items-center justify-center gap-3 z-10 rounded-3xl">
          <p className="text-gray-400 text-xs font-bold tracking-[0.3em] uppercase">
            FINAL
          </p>
          <div className="flex items-center gap-5">
            <span
              className="font-black tabular-nums text-5xl"
              style={{ color: ts1.primaryHex }}
            >
              {game.team1_score}
            </span>
            <span className="text-gray-400 font-black text-2xl">–</span>
            <span
              className="font-black tabular-nums text-5xl"
              style={{ color: ts2.primaryHex }}
            >
              {game.team2_score}
            </span>
          </div>
          <div className="bg-gray-800 rounded-full px-4 py-1.5 border border-gray-700">
            <p className="text-gray-300 text-xs font-bold tracking-wider">
              {winnerName ? `${winnerName} wins` : "Tie game"}
            </p>
          </div>
        </div>
      )}

      {/* Card header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-800">
        <span className="text-gray-500 dark:text-gray-400 text-xs font-bold tracking-widest uppercase">
          {halfLabel}
        </span>
        <div className="flex items-center gap-2">
          {game.is_scrimmage && (
            <span className="bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 text-xs font-bold tracking-widest uppercase px-2.5 py-0.5 rounded-full">
              SCRIMMAGE
            </span>
          )}
          {!isFinal && <LivePulse />}
        </div>
      </div>

      {/* Score row */}
      <div className="flex items-center px-5 py-6 gap-3">
        {/* Team 1 */}
        <div className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
          {game.team1?.logo_url ? (
            <img
              src={game.team1.logo_url}
              alt={game.team1.name}
              className="w-12 h-12 rounded-full object-cover border-2"
              style={{ borderColor: logo1BorderColor }}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-black border-2"
              style={{
                ...ts1.headerStyle,
                borderColor: logo1BorderColor,
                color: getTextColorForAccent(ts1.primaryHex, isDark),
              }}
            >
              {(game.team1?.name ?? "?")
                .split(" ")
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
          )}
          <p className="text-gray-900 dark:text-white font-bold text-lg leading-tight text-center truncate w-full px-1">
            {game.team1?.name}
          </p>
          <AnimatedScore score={game.team1_score} color={score1Color} />
        </div>

        <div className="flex flex-col items-center shrink-0 gap-1 text-gray-300 dark:text-gray-700">
          <div className="w-px h-10 bg-gray-200 dark:bg-gray-700" />
          <span className="font-black text-xl">–</span>
          <div className="w-px h-10 bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* Team 2 */}
        <div className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
          {game.team2?.logo_url ? (
            <img
              src={game.team2.logo_url}
              alt={game.team2.name}
              className="w-12 h-12 rounded-full object-cover border-2"
              style={{ borderColor: logo2BorderColor }}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-black border-2"
              style={{
                ...ts2.headerStyle,
                borderColor: logo2BorderColor,
                color: getTextColorForAccent(ts2.primaryHex, isDark),
              }}
            >
              {(game.team2?.name ?? "?")
                .split(" ")
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </div>
          )}
          <p className="text-gray-900 dark:text-white font-bold text-lg leading-tight text-center truncate w-full px-1">
            {game.team2?.name}
          </p>
          <AnimatedScore score={game.team2_score} color={score2Color} />
        </div>
      </div>

      {/* Last Action */}
      <LastAction action={lastAction} />

      {/* Updated at */}
      <div className="border-t border-gray-100 dark:border-gray-800 px-5 py-2.5">
        <p className="text-gray-400 dark:text-gray-500 text-xs text-center">
          {game.updated_at
            ? `Updated ${new Date(game.updated_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" })}`
            : "—"}
        </p>
      </div>
    </div>
  );
};

// ─── MultiGameView ────────────────────────────────────────────────────────────
const MultiGameView = ({ liveGames, finalIds, globalLastAction }) => {
  const gameList = Object.values(liveGames);

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 flex flex-col">
      <div className="flex items-center justify-between px-5 pt-6 pb-4 border-b border-gray-200 dark:border-gray-800">
        <p className="text-orange-600 dark:text-orange-400 text-xs font-bold tracking-[0.3em] uppercase">
          Muqalab Hoops
        </p>
        <p className="text-gray-500 dark:text-gray-400 text-xs">
          {gameList.length} game{gameList.length !== 1 ? "s" : ""} live
        </p>
      </div>

      <div className="flex-1 px-4 pb-8 pt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {gameList.map((game) => (
            <GameCard
              key={game.id}
              game={game}
              isFinal={finalIds.has(game.id)}
              lastAction={game.last_action ?? null}
            />
          ))}
        </div>
      </div>

      <div className="pb-8 text-center">
        <p className="text-gray-400 dark:text-gray-500 text-xs tracking-wider">
          Updates in real time • No refresh needed
        </p>
      </div>
    </div>
  );
};

// ─── PublicScoreboard (root) ──────────────────────────────────────────────────
const PublicScoreboard = () => {
  const [status, setStatus] = useState("loading");
  const [liveGames, setLiveGames] = useState({});
  const [finalIds, setFinalIds] = useState(new Set());
  const [nextGame, setNextGame] = useState(null);

  const channelRef = useRef(null);

  const loadNextGame = useCallback(async () => {
    const { data } = await supabase
      .from("games")
      .select(
        `id, game_date, team1:teams!games_team1_id_fkey(id, name, logo_url), team2:teams!games_team2_id_fkey(id, name, logo_url)`,
      )
      .eq("status", "scheduled")
      .gt("game_date", new Date().toISOString())
      .order("game_date", { ascending: true })
      .limit(1)
      .single();

    if (data) setNextGame(data);
  }, []);

  const handleRow = useCallback(
    (row) => {
      if (row.status === "live") {
        setLiveGames((prev) => ({ ...prev, [row.id]: row }));
        setStatus("live");
      } else if (row.status === "final") {
        setLiveGames((prev) => ({ ...prev, [row.id]: row }));
        setFinalIds((prev) => new Set([...prev, row.id]));

        setTimeout(() => {
          setLiveGames((prev) => {
            const next = { ...prev };
            delete next[row.id];
            if (Object.keys(next).length === 0) {
              setStatus("idle");
              loadNextGame();
            }
            return next;
          });
          setFinalIds((prev) => {
            const n = new Set(prev);
            n.delete(row.id);
            return n;
          });
        }, 8000);
      }
    },
    [loadNextGame],
  );

  const hydrateAndHandle = useCallback(
    async (rawRow) => {
      const { data } = await supabase
        .from("live_games")
        .select(
          `
            id, team1_score, team2_score, half, status, is_scrimmage, updated_at, last_action,
            team1:teams!live_games_team1_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient),
            team2:teams!live_games_team2_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient)
        `,
        )
        .eq("id", rawRow.id)
        .single();

      if (data) handleRow(data);
    },
    [handleRow],
  );

  // ── Mount: initial fetch + subscribe ─────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      const { data, error } = await supabase
        .from("live_games")
        .select(
          `
                id, team1_score, team2_score, half, status,
                is_scrimmage, updated_at, last_action,
                team1:teams!live_games_team1_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient),
                team2:teams!live_games_team2_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient)
            `,
        )
        .eq("status", "live");

      if (!error && data && data.length > 0) {
        const map = {};
        data.forEach((row) => {
          map[row.id] = row;
        });
        setLiveGames(map);
        setStatus("live");
      } else {
        setStatus("idle");
        await loadNextGame();
      }

      // Create channel
      const channel = supabase
        .channel("live_games_all")
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "live_games" },
          (payload) => hydrateAndHandle(payload.new),
        )
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "live_games" },
          (payload) => hydrateAndHandle(payload.new),
        )
        .subscribe();

      channelRef.current = channel;
    };

    init();

    // Proper cleanup — capture the channel value at the time the effect runs
    return () => {
      const currentChannel = channelRef.current;
      if (currentChannel) {
        supabase.removeChannel(currentChannel);
        channelRef.current = null; // optional safety
      }
    };
  }, [hydrateAndHandle, loadNextGame]);

  if (status === "loading") return <LoadingState />;

  if (Object.keys(liveGames).length > 0) {
    return <MultiGameView liveGames={liveGames} finalIds={finalIds} />;
  }

  return <NextGameState nextGame={nextGame} />;
};

export default PublicScoreboard;
